import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ProductListItem } from '@aps/contracts';
import { pickText, useToast } from '@aps/ui';
import { useNavigation } from '../app/navigation.js';
import { useSettings } from '../app/settings.js';
import { canOrder } from '../app/session.js';
import { ApiError, api } from './api.js';
import { haptic } from './telegram.js';

export interface AddToCart {
  /** Tovarni savatga 1 dona qo'shadi (toast + haptic ichida). */
  add: (product: ProductListItem) => void;
  /**
   * Ro'yxat kartochkasidagi tugma o'chiq bo'lishi kerakmi: mehmon
   * (Telegramdan tashqarida buyurtma berib bo'lmaydi) yoki so'rov ketmoqda
   * (ikki marta bosish ikki dona qo'shmasin).
   */
  disabled: boolean;
}

/**
 * Ro'yxatdagi tovar kartochkasidan savatga qo'shish.
 *
 * Mantiq `ProductScreen` dagi bilan bir xil: muvaffaqiyatda haptic + savat
 * keshi yangilanadi + toast (savat boshqa ekranda, natija ko'rinmaydi).
 * Xatoda SERVER matni ko'rsatiladi ("Avval tizimga kiring", "Do'konda
 * yetarli qoldiq yo'q"); `ApiError` bo'lmasa (tarmoq) — zaxira matn.
 *
 * Tugagan va "Tez kunda" tovarni kartochkaning o'zi o'chiradi
 * (`ProductCard`), PRE_ORDER ruxsat etiladi.
 */
export function useAddToCart(): AddToCart {
  const { t, locale } = useSettings();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { switchRoot } = useNavigation();

  const mutation = useMutation({
    mutationFn: (product: ProductListItem) => api.addToCart(product.id, 1),
    onSuccess: (next, product) => {
      haptic('medium');
      queryClient.setQueryData(['cart'], next);
      // Qaysi tovar qo'shilgani va keyingi qadam ("Savatga o'tish") ko'rinadi: mijoz
      // savat boshqa tabda ekanini bilib turadi va bir bosishda o'tadi.
      toast.show(t('toast.addedToCart'), 'success', {
        key: 'cart-add',
        description: pickText(product.nameUz, product.nameRu, locale),
        action: {
          label: t('toast.openCart'),
          onSelect: () => {
            switchRoot('cart');
          },
        },
      });
    },
    onError: (caught: unknown) => {
      toast.show(
        caught instanceof ApiError ? caught.message_(locale) : t('toast.failed'),
        'danger',
      );
    },
  });

  return {
    add: (product) => {
      mutation.mutate(product);
    },
    disabled: !canOrder() || mutation.isPending,
  };
}
